import React, { useCallback } from 'react';
import moment from 'moment';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import validator from 'validator';
import cx from 'classnames';
import _ from 'lodash';
import s from './ProductC2cForm.scss';
import { classes } from './ProductC2cForm.st.css';
import settingsParams from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import { useCascadingSettings } from '../../../Settings/useCascadingSettings';
import { useControllerProps } from '../../ControllerContext';
import { useState } from '../../stores';
import {
  TextField,
  TextArea,
  DatePickerInput,
  DatePickerInputProps,
} from '@wix/wix-ui-tpa';
import { SPECS } from '../../../../../consts';
import { TimeSlotsDropdown } from './TimeInput/TimeSlotsDropdown';
import { useDeliveryDateTimeState } from './useDeliveryDateTimeState';
import {
  toDateValue,
  isDateToday,
  isTimeInPast,
  mergeDateTime,
} from '../../../../../utils/dateAndTime';

const MAX_MESSAGE_LENGTH = 500;
const DEFAULT_TIME_SLOT = '12:00';

export const emailFieldRef = React.createRef<TextField>();

const ProductC2cForm = () => {
  const {
    state: { checkout },
    dispatch,
  } = useState();
  const cascadingSettings = useCascadingSettings();
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const { locale, product } = useControllerProps().giftCardStore;
  const {
    purchase_options_validation,
    show_errors,
    setPurchaseOptionsValidation,
  } = useControllerProps().giftCardCheckoutStore;

  const {
    selectedDate,
    selectedTimeId,
    isToday,
    setSelectedDate,
    setSelectedTimeId,
  } = useDeliveryDateTimeState();

  const shouldUseInputFieldsCornerRadius = experiments.enabled(
    SPECS.RecipientInputFieldsCornerRadius,
  );

  const shouldShowGiftCardExpiration = experiments.enabled(
    SPECS.ShowGiftCardExpiration,
  );

  const isDeliveryTimePickerEnabled = experiments.enabled(
    SPECS.GiftCardDeliveryTimePicker,
  );

  const showDeliverySection = cascadingSettings.get(
    settingsParams.deliveryDate,
    isDeliveryTimePickerEnabled
      ? stylesParams.shouldShowDeliveryDateAndTime
      : stylesParams.shouldShowDeliveryDate,
  );

  const renderDateAndTimePicker = () => {
    if (isDeliveryTimePickerEnabled) {
      return (
        <div className={cx(s.textFieldContainer, s.pickerContainerWrapper)}>
          <div className={s.pickerContainer}>{renderDatePicker()}</div>
          <div className={s.pickerContainer}>{renderDeliveryTimePicker()}</div>
        </div>
      );
    }
    return <div className={s.textFieldContainer}>{renderDatePicker()}</div>;
  };

  const handleDateChange = useCallback(
    (value: string | Date) => {
      if (!isDeliveryTimePickerEnabled) {
        if (!value || moment().isSame(value, 'day')) {
          dispatch({ type: 'setPurchaseOptions', payload: { deliverAt: undefined } });
          return;
        }
        const formattedDate = moment(value)
          .hour(moment().hour())
          .minutes(moment().minutes())
          .utc()
          .toDate();
        dispatch({ type: 'setPurchaseOptions', payload: { deliverAt: formattedDate } });
        return;
      }

      const dateValue = toDateValue(value);
      setSelectedDate(dateValue);

      if (!dateValue) {
        setSelectedTimeId(undefined);
        dispatch({ type: 'setPurchaseOptions', payload: { deliverAt: undefined } });
        return;
      }

      const isNewDayToday = isDateToday(dateValue);
      const isCurrentTimeInvalid =
        !selectedTimeId || (isNewDayToday && isTimeInPast(selectedTimeId));
      const resolvedTimeId = isCurrentTimeInvalid
        ? isNewDayToday
          ? undefined
          : DEFAULT_TIME_SLOT
        : selectedTimeId;

      setSelectedTimeId(resolvedTimeId);
      dispatch({
        type: 'setPurchaseOptions',
        payload: {
          deliverAt: resolvedTimeId ? mergeDateTime(dateValue, resolvedTimeId) : undefined,
        },
      });
    },
    [
      isDeliveryTimePickerEnabled,
      selectedTimeId,
      setSelectedDate,
      setSelectedTimeId,
      dispatch,
    ],
  );

  const renderDatePicker = () => (
    <>
      <div className={s.label}>
        <label>{settings.get(settingsParams.deliveryDateLabel)}</label>
      </div>
      <DatePickerInput
        data-hook="rise-gift-card-date-picker-input"
        excludePastDates={true}
        removeClearButton={isDeliveryTimePickerEnabled}
        locale={locale as DatePickerInputProps['locale']}
        className={cx(s.datePickerInput, classes.datePickerInput, {
          'border-radius': '10px',
        })}
        placeholderText={settings.get(settingsParams.deliveryDateDefaultText)}
        value={
          isDeliveryTimePickerEnabled
            ? selectedDate
            : checkout.purchaseOptions.deliverAt
        }
        dateInputAriaLabel={settings.get(settingsParams.deliveryDateLabel)}
        onChange={handleDateChange}
      />
    </>
  );
  const renderDeliveryTimePicker = () => (
    <>
      <div className={s.label}>
        <label>{settings.get(settingsParams.deliveryTimeLabel)}</label>
      </div>
      <TimeSlotsDropdown
        selectedId={selectedTimeId}
        disabled={!selectedDate}
        isToday={isToday}
        onChange={(option) => {
          if (!option.id || !selectedDate) {
            return;
          }
          setSelectedTimeId(option.id);
          dispatch({
            type: 'setPurchaseOptions',
            payload: { deliverAt: mergeDateTime(selectedDate, option.id) },
          });
        }}
      />
    </>
  );

  return (
    <>
      <div className={s.textFieldContainer}>
        <TextField
          ref={emailFieldRef}
          required={true}
          type="email"
          inputMode="email"
          data-hook="rise-gift-card-recipient-email-field"
          maxLength={100}
          className={
            shouldUseInputFieldsCornerRadius
              ? classes.textField
              : classes.textFieldOld
          }
          value={checkout.purchaseOptions.recipientEmail}
          label={`${settings.get(settingsParams.recipientEmailLabel)}`}
          errorMessage={settings.get(settingsParams.recipientEmailErrorMessage)}
          error={
            show_errors && !_.get(purchase_options_validation, 'recipientEmail')
          }
          autoComplete="email"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            dispatch({
              type: 'setPurchaseOptions',
              payload: {
                recipientEmail: value,
              },
            });
            setPurchaseOptionsValidation(
              'recipientEmail',
              validator.isEmail(value),
            );
          }}
        />
      </div>
      <div className={s.textFieldContainer}>
        <TextField
          data-hook="rise-gift-card-recipient-name-field"
          maxLength={50}
          className={
            shouldUseInputFieldsCornerRadius
              ? classes.textField
              : classes.textFieldOld
          }
          value={checkout.purchaseOptions.recipientName}
          label={settings.get(settingsParams.recipientNameLabel)}
          autoComplete="name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({
              type: 'setPurchaseOptions',
              payload: {
                recipientName: e.target.value,
              },
            });
          }}
        />
      </div>
      {showDeliverySection && renderDateAndTimePicker()}
      {shouldShowGiftCardExpiration && (
        <div
          className={s.expirationContainer}
          data-hook="rise-gift-card-expiration"
        >
          <span>
            {product?.months_until_expired
              ? t('giftCardPage.deliveryDate.expiration.months.note', {
                  monthsNum: product.months_until_expired,
                })
              : t('giftCardPage.deliveryDate.expiration.never.note')}
          </span>
        </div>
      )}
      <div className={s.textFieldContainer}>
        <TextArea
          data-hook="rise-gift-card-message-text-area"
          maxLength={MAX_MESSAGE_LENGTH}
          className={
            shouldUseInputFieldsCornerRadius
              ? classes.textArea
              : classes.textAreaOld
          }
          label={settings.get(settingsParams.giftMessageLabel)}
          onChange={(e) => {
            dispatch({
              type: 'setPurchaseOptions',
              payload: {
                greetingMessage: e.target.value,
              },
            });
          }}
          ariaLabel={settings.get(settingsParams.giftMessageLabel)}
          value={checkout.purchaseOptions.greetingMessage}
        />
      </div>
    </>
  );
};

export default ProductC2cForm;
